import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, UserPlus, ChevronDown, Clock, ShieldCheck,
  Play, Zap, CalendarClock, ListOrdered,
} from 'lucide-react';

import EmployeeRow from './components/EmployeeRow';
import { useEmployees } from './useEmployees';
import { PRIVACY_KEY } from './constants';
import {
  employeeName, computeNextPayrollDue, formatDueDate,
} from './helpers';
import {
  BRAND, BRAND_DARK, BG_CARD, BG_PAGE, BORDER,
  TEXT_INK, TEXT_PRIMARY, TEXT_SECONDARY, WARN_TEXT, TH,
} from './styles';
import type { Employee, ActiveFilter } from './types';

// Untyped JS utils
import { getReadiness as getReadinessRaw } from '../../utils/payrollReadiness';
import { startNewPayroll as startNewPayrollRaw } from '../../utils/payrollLauncher';
const getReadiness = getReadinessRaw as any;
const startNewPayroll = startNewPayrollRaw as any;

const FILTERS: ReadonlyArray<ActiveFilter> = ['Active Employees', 'Inactive Employees', 'All Employees'];

export default function EmployeesList() {
  const navigate = useNavigate();
  const { data: employees = [], isLoading, error } = useEmployees();

  const [search, setSearch] = useState<string>('');
  const [privacy, setPrivacy] = useState<boolean>(localStorage.getItem(PRIVACY_KEY) === 'on');
  const [activeTab, setActiveTab] = useState<'list' | 'directory'>('list');
  const [openMenuId, setOpenMenuId] = useState<string | number | null>(null);
  const [splitMenuOpen, setSplitMenuOpen] = useState<boolean>(false);
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('Active Employees');
  const [searchFocused, setSearchFocused] = useState<boolean>(false);
  const [activeMenuOpen, setActiveMenuOpen] = useState<boolean>(false);

  const splitMenuRef = useRef<HTMLDivElement | null>(null);
  const activeMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!splitMenuOpen && !activeMenuOpen && openMenuId == null) return;
    const onClick = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (splitMenuOpen && (!splitMenuRef.current || !splitMenuRef.current.contains(target))) setSplitMenuOpen(false);
      if (activeMenuOpen && (!activeMenuRef.current || !activeMenuRef.current.contains(target))) setActiveMenuOpen(false);
      setOpenMenuId(null);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [splitMenuOpen, activeMenuOpen, openMenuId]);

  const togglePrivacy = (): void => {
    const next = !privacy;
    setPrivacy(next);
    localStorage.setItem(PRIVACY_KEY, next ? 'on' : 'off');
  };

  const withReadiness: Employee[] = useMemo(
    () => employees.map((e) => {
      const r = getReadiness(e);
      return { ...e, _ready: r && r.ready };
    }),
    [employees],
  );

  const visible: Employee[] = useMemo(
    () => withReadiness.filter((emp) => {
      const s = emp.employment_status || 'active';
      const isActiveGroup = s === 'active' || s === 'paid_leave' || s === 'unpaid_leave';
      const isInactiveGroup = s === 'terminated' || s === 'not_on_payroll' || s === 'deceased';
      if (activeFilter === 'Active Employees' && !isActiveGroup) return false;
      if (activeFilter === 'Inactive Employees' && !isInactiveGroup) return false;
      if (search) {
        const name = employeeName(emp).toLowerCase();
        if (!name.includes(search.toLowerCase())) return false;
      }
      return true;
    }),
    [withReadiness, search, activeFilter],
  );

  const total = withReadiness.length;
  const readyCount = withReadiness.filter((e) => e._ready).length;
  const nextDue = useMemo(() => computeNextPayrollDue(), []);
  const dueLabel = formatDueDate(nextDue);
  const isDueToday = dueLabel.includes('today');

  if (isLoading) {
    return (
      <div style={{
        background: BG_CARD, minHeight: '100vh',
        padding: 40, textAlign: 'center',
        color: TEXT_SECONDARY, fontFamily: 'inherit',
      }}>
        Loading employees...
      </div>
    );
  }

  return (
    <div style={{ background: BG_CARD, minHeight: '100vh', width: '100%', fontFamily: 'inherit' }}>
      <div style={{ padding: '24px 28px 36px', width: '100%' }}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          marginBottom: 18, gap: 16, flexWrap: 'wrap',
        }}>
          <div>
            <h1 style={{
              fontSize: 34, fontWeight: 700, color: TEXT_INK,
              margin: '0 0 6px', letterSpacing: '-0.02em',
            }}>Employees</h1>
            <div style={{ fontSize: 14, color: TEXT_INK, fontWeight: 500 }}>
              Manage your team's profiles, pay, tax info, credentials, and direct deposit.
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 7 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <a onClick={() => navigate('/payroll/paycheques')} style={{ fontSize: 13, fontWeight: 600, color: BRAND_DARK, cursor: 'pointer' }}>Paycheque list</a>
              <a onClick={() => alert('Settings coming next')} style={{ fontSize: 13, fontWeight: 600, color: BRAND_DARK, cursor: 'pointer' }}>Settings</a>
            </div>
            <div ref={splitMenuRef} style={{ position: 'relative', display: 'inline-flex', alignItems: 'stretch' }}>
              <button onClick={() => startNewPayroll(navigate)} style={{
                background: BRAND, color: 'white', fontSize: 14, fontWeight: 600,
                padding: '11px 18px', border: 'none', borderRadius: '9px 0 0 9px',
                cursor: 'pointer', fontFamily: 'inherit',
              }}>Run payroll</button>
              <button onClick={() => setSplitMenuOpen(!splitMenuOpen)} style={{
                background: BRAND, color: 'white', border: 'none',
                borderLeft: '1px solid rgba(255,255,255,0.25)',
                borderRadius: '0 9px 9px 0', padding: '0 10px',
                display: 'grid', placeItems: 'center', cursor: 'pointer',
              }}><ChevronDown size={16} /></button>
              {splitMenuOpen && (
                <div style={{
                  position: 'absolute', right: 0, top: 46,
                  background: BG_CARD, border: '0.5px solid ' + BORDER,
                  borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  padding: 4, minWidth: 220, zIndex: 50,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 5, cursor: 'pointer', fontSize: 13, color: TEXT_PRIMARY }}
                    onClick={() => { setSplitMenuOpen(false); startNewPayroll(navigate); }}>
                    <Play size={14} style={{ color: TEXT_SECONDARY }} />Run scheduled payroll
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 5, cursor: 'pointer', fontSize: 13, color: TEXT_PRIMARY }}
                    onClick={() => { setSplitMenuOpen(false); alert('Off-cycle payroll coming soon'); }}>
                    <Zap size={14} style={{ color: TEXT_SECONDARY }} />Run off-cycle payroll
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 5, cursor: 'pointer', fontSize: 13, color: TEXT_PRIMARY }}
                    onClick={() => { setSplitMenuOpen(false); navigate('/payroll/items?expand=schedules'); }}>
                    <CalendarClock size={14} style={{ color: TEXT_SECONDARY }} />Manage pay schedules
                  </div>
                </div>
              )}
            </div>
            <div style={{ fontSize: 12.5, color: isDueToday ? WARN_TEXT : TEXT_SECONDARY, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Clock size={14} />{dueLabel}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, borderBottom: '1.5px solid ' + TEXT_INK, marginBottom: 18 }}>
          <div onClick={() => setActiveTab('list')} style={{
            padding: '9px 14px', fontSize: 14, fontWeight: 600,
            color: activeTab === 'list' ? BRAND_DARK : TEXT_SECONDARY,
            cursor: 'pointer',
            borderBottom: activeTab === 'list' ? '3px solid ' + TEXT_INK : '3px solid transparent',
            marginBottom: -1,
          }}>List</div>
          <div onClick={() => navigate('/payroll/employees/directory')} style={{
            padding: '9px 14px', fontSize: 14, fontWeight: 600,
            color: TEXT_SECONDARY, cursor: 'pointer',
            borderBottom: '3px solid transparent', marginBottom: -1,
          }}>Directory</div>
        </div>

        {/* Readiness banner */}
        <div style={{
          background: BG_CARD, border: '0.5px solid ' + BORDER, borderRadius: 10,
          padding: '13px 16px', marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 14, fontWeight: 600, color: TEXT_INK }}>
            <ShieldCheck size={18} style={{ color: BRAND }} />
            <span><strong>{readyCount} of {total}</strong> employee{total === 1 ? '' : 's'} ready for payroll</span>
          </div>
        </div>

        {/* Toolbar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 20, marginBottom: 14, flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{
              width: searchFocused ? 420 : 260, height: 42,
              display: 'flex', alignItems: 'center', gap: 8,
              border: searchFocused ? '1px solid ' + BRAND_DARK : '1px solid ' + BORDER,
              borderRadius: 999, padding: '0 16px', fontSize: 14,
              background: '#FFFFFF', transition: 'width 0.25s, border-color 0.15s',
            }}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder="Find an employee"
                style={{
                  flex: 1, border: 'none', outline: 'none',
                  fontFamily: 'inherit', fontSize: 14, fontWeight: 500,
                  color: TEXT_PRIMARY, background: 'transparent',
                }}
              />
              <Search size={16} style={{ color: '#94A0B2' }} />
            </div>
            <div ref={activeMenuRef} style={{ position: 'relative' }}>
              <button onClick={() => setActiveMenuOpen(!activeMenuOpen)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                fontSize: 14, fontWeight: 600, color: TEXT_INK,
                padding: '0 18px', height: 42,
                border: activeMenuOpen ? '1px solid ' + BRAND : '1px solid ' + BORDER,
                borderRadius: 999, cursor: 'pointer',
                background: activeMenuOpen ? '#E1F5EE' : '#FFFFFF',
                fontFamily: 'inherit', whiteSpace: 'nowrap',
              }}>
                {activeFilter}
                <ChevronDown size={16} strokeWidth={2} style={{
                  transform: activeMenuOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.15s',
                }} />
              </button>
              {activeMenuOpen && (
                <div style={{
                  position: 'absolute', top: 44, left: 0,
                  background: BG_CARD, border: '0.5px solid ' + BORDER,
                  borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  padding: 4, minWidth: 210, zIndex: 50,
                }}>
                  {FILTERS.map((opt) => (
                    <div
                      key={opt}
                      onClick={() => { setActiveFilter(opt); setActiveMenuOpen(false); }}
                      onMouseEnter={(e) => { if (activeFilter !== opt) (e.currentTarget as HTMLDivElement).style.background = '#F8F9FA'; }}
                      onMouseLeave={(e) => { if (activeFilter !== opt) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                      style={{
                        display: 'flex', alignItems: 'center', height: 40,
                        padding: '0 18px', cursor: 'pointer', fontSize: 14,
                        color: TEXT_INK, fontWeight: activeFilter === opt ? 600 : 500,
                        background: activeFilter === opt ? '#E1F5EE' : 'transparent',
                        whiteSpace: 'nowrap',
                      }}
                    >{opt}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span onClick={togglePrivacy} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              fontSize: 13.5, fontWeight: 700, color: TEXT_INK,
              cursor: 'pointer', padding: '9px 14px',
              border: '1.5px solid ' + TEXT_INK, borderRadius: 9, background: BG_CARD,
            }}>
              <span style={{
                width: 38, height: 22, borderRadius: 20,
                background: privacy ? BRAND : BORDER,
                position: 'relative', transition: '0.15s',
              }}>
                <span style={{
                  position: 'absolute', top: 2, left: privacy ? 18 : 2,
                  width: 18, height: 18, borderRadius: '50%',
                  background: 'white', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                  transition: '0.15s',
                }} />
              </span>
              Privacy
            </span>
            <button onClick={() => navigate('/payroll/items')} style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              fontSize: 13.5, fontWeight: 700, color: TEXT_PRIMARY,
              padding: '9px 13px', border: '1.5px solid ' + TEXT_INK, borderRadius: 9,
              cursor: 'pointer', background: BG_CARD, fontFamily: 'inherit', whiteSpace: 'nowrap',
            }}>
              <ListOrdered size={16} />Payroll items
            </button>
            <button onClick={() => navigate('/payroll/employees/add')} style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              fontSize: 13.5, fontWeight: 700, color: '#fff',
              padding: '9px 16px', border: 0, borderRadius: 9,
              cursor: 'pointer', background: TEXT_INK,
              fontFamily: 'inherit', whiteSpace: 'nowrap',
            }}>
              <UserPlus size={16} />Add employee
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            padding: 12, background: '#FEE2E2',
            border: '0.5px solid #F87171', borderRadius: 8,
            color: '#991B1B', fontSize: 13, marginBottom: 14,
          }}>
            <strong>Could not load:</strong> {error.message}
          </div>
        )}

        {/* Table */}
        <div style={{ border: '0.5px solid ' + BORDER, borderRadius: 10, overflow: 'visible' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...TH, paddingLeft: 18 }}>Name</th>
                <th style={TH}>Pay rate</th>
                <th style={TH}>Pay method</th>
                <th style={TH}>Available vacation</th>
                <th style={TH}>Email</th>
                <th style={TH}>Job title</th>
                <th style={TH}>Phone number</th>
                <th style={TH}>Status</th>
                <th style={{ ...TH, textAlign: 'center', width: 54 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: 40, textAlign: 'center', color: TEXT_SECONDARY, fontSize: 13 }}>
                    {total === 0 ? 'No employees yet. ' : 'No employees match your search. '}
                    <a onClick={() => navigate('/payroll/employees/add')} style={{ color: BRAND, cursor: 'pointer', fontWeight: 600 }}>Add an employee</a> to start.
                  </td>
                </tr>
              ) : visible.map((emp) => (
                <EmployeeRow
                  key={String(emp.id)}
                  emp={emp}
                  privacy={privacy}
                  isMenuOpen={openMenuId === emp.id}
                  onMenuToggle={(id) => setOpenMenuId(id)}
                />
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ fontSize: 12.5, color: TEXT_SECONDARY, marginTop: 12 }}>
          Showing {visible.length} of {total} employee{total === 1 ? '' : 's'}
        </div>

      </div>
    </div>
  );
}